import {DateTime} from 'luxon'

import {Jurisdiction} from '../countries/constants'
import {routeTo} from '../router'
import {IpOfficePriceIncreaseAlertContentType} from './types'

export enum RegistrationRequestStatus {
  // verificationStatuses
  verificationRequested = 'Verification requested',
  verificationApproved = 'Verification approved',
  verificationDismissed = 'Verification dismissed',
  orderCreated = 'Order created',
  readyForDrafting = 'Ready for drafting',

  // draftingStatuses
  sentForReview = 'Sent for review',
  reviewRequested = 'Review requested',
  applicationPostponed = 'Application postponed',
  applicationApproved = 'Application approved',

  // followupStatuses = {
  lostToFollowup = 'Lost to follow-up',
  clientUnresponsive = 'Client unresponsive',
  refunded = 'Refunded',
}

export enum RegistrationRequestAction {
  // verificationActions
  verificationRepeated = 'Verification repeated',
  tooManyVerifications = 'Too many verifications reminder',
  specifyGoodsAndServicesReminder = 'Specify goods and services reminder',
  verificationResultViewed = 'Verification result viewed',
  invoiceNotPaidReminder = 'Invoice not paid reminder',
  invoicePaid = 'Invoice paid',
  checkoutCompleted = 'Checkout completed',

  // draftingActions
  workInProgressNotification = 'Work in progress notification',
  summaryNotApprovedReminder = 'Summary not approved reminder',
  applicationDraftFirstReminder = 'Application draft first reminder',
  applicationDraftSecondReminder = 'Application draft second reminder',
  applicationDraftThirdReminder = 'Application draft third reminder',
  applicationDraftSmsReminder = 'Application draft sms reminder',
  scamWarningReminder = 'Scam warning reminder',

  // rest
  verificationStarted = 'Verification started',
  orderStarted = 'Registration order started',
  urgingSmsSent = 'Urging sms sent',
  resultNotViewedFirstReminder = 'Result not viewed first reminder',
  resultNotViewedSecondReminder = 'Result not viewed second reminder',
  verificationApprovedFirstReminder = 'Verification approved first reminder',
  verificationApprovedSecondReminder = 'Verification approved second reminder',
  verificationApprovedThirdReminder = 'Verification approved third reminder',
  verificationApprovedFourthReminder = 'Verification approved fourth reminder',
  verificationDismissedFirstReminder = 'Verification dismissed first reminder',
  checkoutNotCompletedFirstReminder = 'Checkout not completed first reminder',
  registrationStartedFirstReminder = 'Registration started first reminder',
  serviceDescriptionUpdatedByUser = 'Service description updated by user',
  trustpilotReviewRequested = 'Trustpilot review requested',
  applicationNotSubmittedReminder = 'Application not submitted reminder',
  reportViolationLinkSent = 'Report violation link sent',
}

export const verificationGroupStatuses = [
  RegistrationRequestStatus.verificationRequested,
  RegistrationRequestStatus.verificationApproved,
  RegistrationRequestStatus.verificationDismissed,
]

export const verificationStatuses = [
  RegistrationRequestStatus.verificationRequested,
  RegistrationRequestStatus.verificationApproved,
  RegistrationRequestStatus.verificationDismissed,
  RegistrationRequestStatus.orderCreated,
  RegistrationRequestStatus.readyForDrafting,
]

export const verificationActions = [
  RegistrationRequestAction.verificationRepeated,
  RegistrationRequestAction.tooManyVerifications,
  RegistrationRequestAction.specifyGoodsAndServicesReminder,
  RegistrationRequestAction.verificationResultViewed,
  RegistrationRequestAction.invoiceNotPaidReminder,
  RegistrationRequestAction.invoicePaid,
  RegistrationRequestAction.checkoutCompleted,
]

export const draftingStatuses = [
  RegistrationRequestStatus.readyForDrafting,
  RegistrationRequestStatus.sentForReview,
  RegistrationRequestStatus.reviewRequested,
  RegistrationRequestStatus.applicationPostponed,
  RegistrationRequestStatus.applicationApproved,
]

export const draftingActions = [
  RegistrationRequestAction.invoicePaid,
  RegistrationRequestAction.workInProgressNotification,
  RegistrationRequestAction.summaryNotApprovedReminder,
  RegistrationRequestAction.invoiceNotPaidReminder,
  RegistrationRequestAction.applicationDraftFirstReminder,
  RegistrationRequestAction.applicationDraftSecondReminder,
  RegistrationRequestAction.applicationDraftThirdReminder,
  RegistrationRequestAction.applicationDraftSmsReminder,
  RegistrationRequestAction.scamWarningReminder,
]

export const followupStatuses = [
  RegistrationRequestStatus.lostToFollowup,
  RegistrationRequestStatus.clientUnresponsive,
  RegistrationRequestStatus.refunded,
]

export const verificationResultValidityMonths = 6
export const verificationResultAdminValidityMonths = 3

export const ipOfficePriceIncreaseInfo: Partial<
  Record<Jurisdiction, IpOfficePriceIncreaseAlertContentType>
> = {
  [Jurisdiction.US]: {
    ipOffice: 'USPTO',
    date: DateTime.fromObject({year: 2025, month: 1, day: 7}),
    linkHref: routeTo.blog.industryArticle(
      'legal',
      'file-your-trademark-now-major-uspto-fee-increase-coming-in-2025',
    ),
    isVisibleToClient: false,
  },
  [Jurisdiction.UK]: {
    ipOffice: 'UKIPO',
    date: DateTime.fromObject({year: 2026, month: 3, day: 24}),
    linkHref: routeTo.blog.article('ukipo-fee-increase-how-to-prepare-your-uk-trademark-in-2026'),
    isVisibleToClient: false,
  },
}

export enum RegistrationRequestTrackingActionId {
  verificationProcess = 'verificationProcess',
  quickDismiss = 'quickDismiss',
  revertDismiss = 'revertDismiss',
  copySummaryLink = 'copySummaryLink',
  copyRegistrationLink = 'copyRegistrationLink',
  readyForDrafting = 'readyForDrafting',
  revertReadyForDrafting = 'revertReadyForDrafting',
  markAsPaid = 'markAsPaid',
  sendOrderDocuments = 'sendOrderDocuments',
  sendForReview = 'sendForReview',
  clientUnresponsive = 'clientUnresponsive',
  revertClientUnresponsive = 'revertClientUnresponsive',
  applicationPostponed = 'applicationPostponed',
  revertApplicationPostponed = 'revertApplicationPostponed',
  resumeApplicationPostponed = 'resumeApplicationPostponed',
  resendForReview = 'resendForReview',
  markApplicationApproved = 'markApplicationApproved',
  markReviewRequested = 'markReviewRequested',
}

export const enabledApplicationDraft = [
  {from: '2022-11-23', countries: [Jurisdiction.EU, Jurisdiction.UK]},
  {
    from: '2022-12-07',
    countries: [
      Jurisdiction.CZ,
      Jurisdiction.SK,
      Jurisdiction.DE,
      Jurisdiction.TR,
      Jurisdiction.BX,
      Jurisdiction.ES,
      Jurisdiction.FR,
      Jurisdiction.IT,
      Jurisdiction.IE,
      Jurisdiction.SG,
      Jurisdiction.HK,
    ],
  },
  {
    from: '2023-01-20',
    countries: Object.values(Jurisdiction).filter((country) => country !== Jurisdiction.US),
  },
  {from: '2023-02-09', countries: [Jurisdiction.US]},
]
